// Achievements on the profile, and the overview's habit metrics. The
// catalogue itself (thresholds, levels, egg rewards) and the progress rules
// live in core (src/achievements/achievements.js), shared with the server
// that decides unlocks and pays their eggs.
// Tone: player-facing and a bit silly: this is a worm game.
import { displayRating } from "./rating";
import * as ach from "core/src/achievements/achievements.js";
import { RANKED_PLACEMENT_GAMES, type RatingRow, type AchievementHistoryRow, type RankedMode } from "./store";

// Everything achievements can judge, derived once from the raw rows.
export interface AchievementMetrics {
  games: number; // casual + ranked
  wins: number;
  podiums: number; // top 3 in a field of 6+ (a top 3 of four is no feat)
  peak: number; // best display rating on any ladder (ranked once placed)
  casualGames: number;
  placementPct: number | null; // casual
  bigWinField: number; // biggest field ever WON
  biggestClimb: number; // best single-match display-rating gain
  biggestDrop: number; // worst single-match loss (positive number)
  maxGamesInDay: number;
  longestStreakDays: number; // consecutive calendar days with at least one match
}

// A field must be at least this big for a top 3 to count as a podium.
const PODIUM_MIN_FIELD = 6;

// `history` is every match on record, oldest first; null when it could not be
// loaded, and then the rating rows' own totals stand in where they can.
export function deriveMetrics(
  row: RatingRow | null,
  ranked: Partial<Record<RankedMode, RatingRow | null>> | null,
  history: AchievementHistoryRow[] | null,
): AchievementMetrics {
  const rankedRows = Object.values(ranked || {}).filter(Boolean) as RatingRow[];
  let bigWinField = 0, biggestClimb = 0, biggestDrop = 0, wins = 0, podiums = 0;
  const perDay = new Map<string, number>();
  const seen: Record<string, number> = {}; // matches so far per ranked ladder
  for (const h of history || []) {
    if (h.placement === 1) { wins++; bigWinField = Math.max(bigWinField, h.fieldSize); }
    if (h.placement <= 3 && h.fieldSize >= PODIUM_MIN_FIELD) podiums++;
    // A ranked ladder's rating is hidden through its placements: its moves
    // there are not climbs or drops anyone saw.
    const placing = h.ranked != null && (seen[h.ranked] = (seen[h.ranked] || 0) + 1) <= RANKED_PLACEMENT_GAMES;
    if (!placing) {
      const delta = displayRating({ mu: h.muAfter, sigma: 0 }) - displayRating({ mu: h.muBefore, sigma: 0 });
      biggestClimb = Math.max(biggestClimb, delta);
      biggestDrop = Math.max(biggestDrop, -delta);
    }
    // the player's own calendar day, not UTC's
    const d = new Date(h.createdAt);
    const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    perDay.set(day, (perDay.get(day) || 0) + 1);
  }
  // Longest run of consecutive days with at least one match (noon to noon, so
  // a daylight-saving day is still one day).
  const days = [...perDay.keys()].sort();
  let longestStreakDays = days.length ? 1 : 0, run = 1;
  for (let i = 1; i < days.length; i++) {
    const gap = Math.round((Date.parse(days[i] + "T12:00:00") - Date.parse(days[i - 1] + "T12:00:00")) / 86400000);
    run = gap === 1 ? run + 1 : 1;
    longestStreakDays = Math.max(longestStreakDays, run);
  }
  const rowGames = (row?.games ?? 0) + rankedRows.reduce((n, r) => n + r.games, 0);
  const peaks = [row?.peak ?? 0, ...rankedRows.filter((r) => r.games >= RANKED_PLACEMENT_GAMES).map((r) => r.peak)];
  return {
    // the rows count every match even when the history could not load
    games: Math.max(rowGames, history?.length ?? 0),
    wins: history ? wins : (row?.wins ?? 0) + rankedRows.reduce((n, r) => n + r.wins, 0),
    podiums: history ? podiums : row?.podiums ?? 0,
    peak: Math.max(...peaks),
    casualGames: row?.games ?? 0,
    placementPct: row?.placementPct ?? null,
    bigWinField,
    biggestClimb,
    biggestDrop,
    maxGamesInDay: Math.max(0, ...perDay.values()),
    longestStreakDays,
  };
}

// ---- achievements v2 (core src/achievements/achievements.js) ----
// The server keeps each player's progress (player_achievements) and pays the
// eggs. Until it has seen a player (or when that row can't be read) the profile
// works the same progress out from their match history, exactly the way the
// server's first-time backfill does (edge-server achievements.js historyMatch).

const PLACEMENT_GAMES = RANKED_PLACEMENT_GAMES;

export function progressFromHistory(history: AchievementHistoryRow[]): any {
  const seen: Record<string, number> = {};
  const casual = { games: 0, placeScore: 0 };
  let p = ach.emptyProgress();
  for (const h of history) {
    let placing = false, hatching = false, casualAfter: { games: number; placeScore: number } | null = null;
    if (h.ranked) {
      seen[h.ranked] = (seen[h.ranked] || 0) + 1;
      placing = seen[h.ranked] < PLACEMENT_GAMES;
      hatching = seen[h.ranked] === PLACEMENT_GAMES;
    } else {
      casual.games += 1;
      casual.placeScore += h.fieldSize > 1 ? (h.fieldSize - h.placement) / (h.fieldSize - 1) : 0;
      casualAfter = { ...casual };
    }
    p = ach.foldMatch(p, {
      ranked: h.ranked,
      placement: h.placement,
      fieldSize: h.fieldSize,
      ratingBefore: displayRating({ mu: h.muBefore, sigma: 0 }),
      ratingAfter: displayRating({ mu: h.muAfter, sigma: 0 }),
      placing,
      hatching,
      day: localDay(h.createdAt),
      stats: h.stats ?? null,
      casual: casualAfter,
    });
  }
  return p;
}

function localDay(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export interface AchievementView {
  id: string;
  icon: string;
  name: string; // with the reached level's numeral ("Certified Worm II")
  baseName: string;
  group: string;
  levels: number; // how many it has
  level: number; // reached (0 = none)
  goal: string; // the next level's ask, or the last one's when all are done
  value: number; // progress toward the next level
  from: number; // the reached level's threshold (0 before the first)
  to: number | null; // the next level's threshold (null when all are done)
  nextEggs: number | null;
  earnedEggs: number; // what the reached levels are worth
  done: boolean;
}

// What the profile shows for each achievement, from a progress object and the
// levels on record (the server's, which never go back; else worked out).
export function viewAchievements(progress: any, levels?: Record<string, number> | null): AchievementView[] {
  return (ach.ACHIEVEMENTS as any[]).map((a) => {
    const level = Math.max(levels?.[a.id] ?? 0, ach.levelOf(a, progress));
    const done = level >= a.levels.length;
    const next = done ? a.levels.length - 1 : level;
    const raw = (progress && progress[a.metric]) || 0;
    return {
      id: a.id,
      icon: a.icon,
      name: level ? ach.levelName(a, level) : a.name,
      baseName: a.name,
      group: a.group,
      levels: a.levels.length,
      level,
      goal: a.goal(a.levels[next], next),
      value: Math.min(raw, done ? a.levels[a.levels.length - 1] : a.levels[next]),
      // (Climber's bar starts at Gold, where every worm hatches)
      from: level ? a.levels[level - 1] : a.metric === "peak" ? 1000 : 0,
      to: done ? null : a.levels[next],
      nextEggs: done ? null : a.eggs[next],
      earnedEggs: a.eggs.slice(0, level).reduce((x: number, y: number) => x + y, 0),
      done,
    };
  });
}

export const ACHIEVEMENT_GROUPS: string[] = [...new Set((ach.ACHIEVEMENTS as any[]).map((a) => a.group as string))];
